import { useQuery } from "@tanstack/react-query";
import { OWGameResponse } from "@devour/client";
import { useSelector } from "react-redux";
import { IStore } from "../../redux/defaultStore";
import getConfig from "../../utils/getConfig";

interface UserGameStats {
    totalXP?: number;
    level?: number;
    hoursPlayed?: number;
    matchesPlayed?: number;
    victories?: number;
    currentStreak?: number;
}

interface UseGameDataResult {
    game: OWGameResponse | null;
    loading: boolean;
    error: Error | null;
    userStats: UserGameStats | null;
}

async function fetchGameBySlug(slug: string): Promise<OWGameResponse> {
    const response = await fetch(`${getConfig().basePath}/overwolf/games/by-slug/${slug}`);

    if (!response.ok) {
        throw new Error(`Failed to fetch game: ${response.statusText}`);
    }

    return response.json();
}

async function fetchUserGameStats(slug: string, token: string): Promise<UserGameStats> {
    // TODO: Implement actual API call when endpoint is ready
    return {
        totalXP: 1250,
        level: 12,
        hoursPlayed: 48,
        matchesPlayed: 234,
        victories: 87,
        currentStreak: 3,
    };
}

export const useGameData = (slug: string): UseGameDataResult => {
    const fullToken = useSelector((store: IStore) => store.metaStore?.fullToken);
    const user = useSelector((store: IStore) => store.userStore?.user);

    // Main game data query with React Query
    const gameQuery = useQuery({
        queryKey: ["game", slug],
        queryFn: () => fetchGameBySlug(slug),
        enabled: !!slug,
        staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
        gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
        retry: 1,
    });

    // User stats query (only if logged in)
    const statsQuery = useQuery({
        queryKey: ["game-stats", slug, user?.id],
        queryFn: () => fetchUserGameStats(slug, fullToken!),
        enabled: !!slug && !!fullToken && !!user,
        staleTime: 2 * 60 * 1000, // Stats are fresh for 2 minutes
        gcTime: 5 * 60 * 1000, // Keep stats in cache for 5 minutes
    });

    return {
        game: gameQuery.data || null,
        loading: gameQuery.isLoading,
        error: gameQuery.error as Error | null,
        userStats: statsQuery.data || null,
    };
};